import React from 'react';
import { SystemStatus } from '../services/api';

interface StatusIndicatorProps {
  status: SystemStatus | null;
}

const StatusIndicator: React.FC<StatusIndicatorProps> = ({ status }) => {
  const getStatusConfig = () => {
    if (!status) {
      return {
        color: 'slate',
        bgGradient: 'from-slate-600 to-slate-700',
        text: 'Unknown',
        icon: '❓',
        description: 'Status unavailable'
      };
    }
    if (status.killSwitchEnabled) {
      return {
        color: 'red',
        bgGradient: 'from-red-600 to-red-700',
        text: 'Stopped',
        icon: '⛔',
        description: 'Kill switch is enabled'
      };
    }
    if (status.schedulerRunning) {
      return {
        color: 'green',
        bgGradient: 'from-green-600 to-emerald-700',
        text: 'Running',
        icon: '✓',
        description: 'Scheduler is active'
      };
    }
    return {
      color: 'yellow',
      bgGradient: 'from-yellow-600 to-amber-700',
      text: 'Paused',
      icon: '⏸',
      description: 'Scheduler is paused'
    };
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return 'Never';
    const date = new Date(dateString);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  };

  const config = getStatusConfig();

  return (
    <div className="bg-slate-800/60 backdrop-blur-sm rounded-xl border border-slate-700/50 p-6 shadow-xl hover:shadow-2xl transition-all duration-300">
      <div className="flex items-start justify-between mb-6">
        <div className="flex-1">
          <h2 className="text-sm font-medium text-slate-400 uppercase tracking-wider mb-1">System Status</h2>
          <div className="flex items-center space-x-3 mt-2">
            <div className={`w-3 h-3 rounded-full bg-${config.color}-500 ${status?.schedulerRunning ? 'animate-pulse' : ''}`}></div>
            <p className="text-2xl font-bold text-white">{config.text}</p>
            {status?.simulationMode && (
              <span className="px-2 py-0.5 bg-yellow-500/20 border border-yellow-500/50 text-yellow-400 text-xs font-semibold rounded-md">
                SIM
              </span>
            )}
          </div>
        </div>
        <div className={`w-12 h-12 rounded-lg bg-gradient-to-br ${config.bgGradient} flex items-center justify-center text-white text-xl shadow-lg`}>
          {config.icon}
        </div>
      </div>
      
      <div className="space-y-4">
        <div className="bg-slate-900/50 rounded-lg p-3">
          <p className="text-xs text-slate-400 mb-1">Status Description</p>
          <p className="text-sm text-slate-200">{config.description}</p>
        </div>

        {status?.simulationMode && (
          <div className="bg-yellow-500/10 border border-yellow-500/30 rounded-lg p-3">
            <p className="text-xs text-yellow-400 mb-1 font-semibold">⚠️ Simulation Mode</p>
            <p className="text-xs text-yellow-300/80">Trades are simulated • No real money is used</p>
          </div>
        )}

        <div className="space-y-3 pt-4 border-t border-slate-700/50">
          <div className="flex justify-between items-center">
            <span className="text-xs text-slate-400">Last Trade</span>
            <span className="text-sm text-slate-200 font-mono">{formatDate(status?.lastTradeDate || null)}</span>
          </div>
          <div className="flex justify-between items-center">
            <span className="text-xs text-slate-400">Last Update</span>
            <span className="text-sm text-slate-200 font-mono">{formatDate(status?.lastUpdate || null)}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StatusIndicator;
